'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string;
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const CONSENT_KEY = 'labcallmanager_consent_v1';

export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr';
  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const euTZPrefixes = ['Europe/', 'Atlantic/Azores', 'Atlantic/Canary', 'Atlantic/Faroe', 'Atlantic/Madeira', 'Arctic/Longyearbyen'];
  const usTZPrefixes = ['America/'];
  const euCountryCodes = ['AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','GB','IS','LI','NO','CH'];
  const isEU = euTZPrefixes.some(p => tz.startsWith(p)) || euCountryCodes.some(c => lang.toUpperCase().endsWith('-' + c));
  const isUS = usTZPrefixes.some(p => tz.startsWith(p)) && (lang.startsWith('en-US') || lang.startsWith('en-CA'));
  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

export function saveConsent(choices: ConsentRecord['choices'], region: ConsentRegion): void {
  if (typeof window === 'undefined') return;
  const record: ConsentRecord = { version: '1.0', timestamp: new Date().toISOString(), region, choices, userAgent: navigator.userAgent };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(record)); } catch (e) { console.error('Failed to save consent:', e); }
}

export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;
  try { const stored = localStorage.getItem(CONSENT_KEY); if (!stored) return null; return JSON.parse(stored) as ConsentRecord; } catch (e) { console.error('Failed to retrieve consent:', e); return null; }
}

export function hasConsent(category: keyof ConsentRecord['choices']): boolean {
  const consent = getConsent();
  if (!consent) return false;
  return consent.choices[category] === true;
}

export function clearConsent(): void {
  if (typeof window === 'undefined') return;
  try { localStorage.removeItem(CONSENT_KEY); } catch (e) { console.error('Failed to clear consent:', e); }
}

export function logConsentAction(action: 'accept_all' | 'reject_all' | 'save_preferences' | 'dismiss', region: ConsentRegion, choices?: ConsentRecord['choices']): void {
  if (typeof window === 'undefined') return;
  const logEntry = { timestamp: new Date().toISOString(), action, region, choices: choices || null, url: window.location.href };
  try { if (process.env.NODE_ENV === 'development') { console.log('[Consent Log]', logEntry); } } catch (e) { console.error('Failed to log consent action:', e); }
}
